import {
  createExpression,
  isExpressionValue,
} from './expression-factory.helpers';
import { isNumeric } from './math.helpers';
import type {
  IElementQuantityExpressionProperty,
  IElementQuantityProperty,
} from '../models/element-quantities.model';
import { DEFAULT_QUANTITY_EXPRESSION } from '../models/element-quantities.model';
import type { ExpressionValue } from '../models/project.model';

/**
 * Whether a quantity expression line is a formula/inherit string (not a plain numeric literal).
 */
export const isNonLiteralQuantityExpression = (
  expression: string | undefined,
): boolean => {
  const line = expression?.trim() ?? '';
  return line.length > 0 && !isNumeric(line);
};

/**
 * Whether an expression line is a typed number (e.g. `"55.3"`), not a formula or the
 * empty/{@link DEFAULT_QUANTITY_EXPRESSION} placeholder.
 */
export const isNumericLiteralCountExpression = (
  expression: string | undefined,
): boolean => {
  const line = expression?.trim() ?? '';
  if (line.length === 0 || line === DEFAULT_QUANTITY_EXPRESSION) return false;
  return isNumeric(line);
};

/**
 * Parsed number for a {@link isNumericLiteralCountExpression} line; `undefined` when it is not one.
 */
export const parseNumericLiteralCountExpression = (
  expression: string | undefined,
): number | undefined => {
  const line = expression?.trim() ?? '';
  if (!isNumericLiteralCountExpression(line)) return undefined;
  return Number(line);
};

/**
 * Expression line to persist on `count` when the user adds a numeric override while the row
 * only has `fallbackCount` (or inherit-only display). Formula/inherit fallbacks use a literal
 * line so `getParentValue(...)` / `gfa` are not copied onto `count`.
 * Existing `count` formulas are left as-is (including legacy inherit text) — numeric apply
 * validation uses {@link getExpressionLineForNumericOverrideSolve} instead of this persist line.
 */
export const getQuantityValueOverrideExpression = (
  quantity: Pick<IElementQuantityExpressionProperty, 'count' | 'fallbackCount'>,
): string => {
  if (isExpressionValue(quantity.count)) {
    return quantity.count.expression ?? DEFAULT_QUANTITY_EXPRESSION;
  }

  const fallback = isExpressionValue(quantity.fallbackCount)
    ? quantity.fallbackCount
    : undefined;
  if (!fallback) return DEFAULT_QUANTITY_EXPRESSION;

  if (isNonLiteralQuantityExpression(fallback.expression)) {
    return DEFAULT_QUANTITY_EXPRESSION;
  }

  return fallback.expression ?? DEFAULT_QUANTITY_EXPRESSION;
};

/**
 * Expression line to {@link solve} when applying a numeric override.
 * Non-literal formulas (inherit `getParentValue`, `gfa`, `depth * 2`) are replaced with
 * {@link DEFAULT_QUANTITY_EXPRESSION} so a finite override is not rejected when the stored
 * formula is NaN/Infinity — including legacy `count` rows that still hold inherit text.
 */
export const getExpressionLineForNumericOverrideSolve = (
  expressionLine: string,
): string =>
  isNonLiteralQuantityExpression(expressionLine)
    ? DEFAULT_QUANTITY_EXPRESSION
    : expressionLine;

/**
 * Builds the `count` patch for a typed numeric override (Element List row, property form, etc.).
 */
export const buildQuantityValueOverrideCount = (
  quantity: IElementQuantityExpressionProperty,
  valueOverride: number,
): ExpressionValue => {
  const currentCount = isExpressionValue(quantity.count)
    ? quantity.count
    : undefined;
  const fallbackCount = isExpressionValue(quantity.fallbackCount)
    ? quantity.fallbackCount
    : undefined;
  const resolvedBase =
    currentCount?.resolved ?? fallbackCount?.resolved ?? valueOverride;

  return createExpression(
    {
      expression: getQuantityValueOverrideExpression(quantity),
      value: valueOverride,
    },
    resolvedBase,
  );
};

/**
 * Baseline {@link ExpressionValue} to merge when applying {@link ProjectChangeType.ElementQuantityValue}
 * on a row that has no explicit `count` yet.
 */
export const getQuantityValueOverrideCountBootstrap = (
  quantity: IElementQuantityProperty,
): ExpressionValue | undefined => {
  if (isExpressionValue(quantity.count)) return quantity.count;

  const fallback = isExpressionValue(quantity.fallbackCount)
    ? quantity.fallbackCount
    : undefined;
  if (!fallback) return undefined;

  if (isNonLiteralQuantityExpression(fallback.expression)) {
    return {
      expression: DEFAULT_QUANTITY_EXPRESSION,
      resolved: fallback.resolved,
      formatted: fallback.formatted,
    };
  }

  return fallback;
};
